import { Text } from '@chakra-ui/react';
// biome-ignore lint/suspicious/noShadowRestrictedNames: migrated from develop
import { delay, escape, isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import { useMutation } from 'urql';

import {
  CREATE_SAVED_SET,
  UPDATE_SAVED_SET,
  type View,
} from '@revelio/data-access';
import { Button, ButtonGroup } from '@revelio/ui';

import { Autocomplete } from '../../../../shared/components/Autocomplete';
import { useToast } from '../../hooks/useToast';
import styles from './CreateFilterSetForm.module.css';
import { type UserSavedFilterSet, useGetStoredSets } from './useGetStoredSets';

export interface FilterSetSaveFormProps {
  view: View;
  onClose: () => void;
  onCreated?: () => void;
  onUpdated?: (item: UserSavedFilterSet) => void;
  isDisabled?: boolean;
  overwriteSlot?: (item: UserSavedFilterSet) => React.ReactNode;
}

export const CreateFilterSetForm = ({
  view,
  onClose,
  onCreated,
  onUpdated,
  isDisabled,
  overwriteSlot,
}: FilterSetSaveFormProps) => {
  const { options: allSavedSets } = useGetStoredSets();

  const [, createSavedSet] = useMutation(CREATE_SAVED_SET);
  const [, updateSavedSet] = useMutation(UPDATE_SAVED_SET);

  const toast = useToast();

  // Read the live URL at save time. useLocation() can be a stale closure here:
  // under load the save fires before React re-renders with the post-selection
  // location, so the hook value would persist the pre-selection filters.
  const currentFiltersUrl = () =>
    `${window.location.pathname}${window.location.search}`;

  const [nameInput, setNameInput] = useState<string>('');
  const [suggestions, setSuggestions] = useState<UserSavedFilterSet[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const itemToString = (item: UserSavedFilterSet | null) =>
    item ? item.name : '';

  const initialFocusRef = useRef<HTMLInputElement>(null);
  const [itemToOverwrite, setItemToOverwrite] = useState<
    UserSavedFilterSet | undefined
  >(undefined);

  const showToast = (title: string, status: 'success' | 'error') => {
    toast({
      title,
      status,
      position: 'top-right',
      duration: 4000,
      isClosable: true,
    });
  };

  const handleUpdateFilterSet = () => {
    if (!itemToOverwrite) return;
    setIsLoading(true);
    updateSavedSet({
      id: itemToOverwrite.id,
      name: escape(nameInput),
      view,
      filters: currentFiltersUrl(),
    })
      .then((res) => {
        if (res.error) {
          showToast('Error updating filter set', 'error');
        } else {
          showToast('Filter set updated', 'success');
        }
      })
      .finally(() => {
        delay(() => {
          setIsLoading(false);
          setItemToOverwrite(undefined);
          onClose();
          onUpdated?.(itemToOverwrite);
        }, 100);
      });
  };

  const handleCreate = () => {
    setIsLoading(true);
    const existing = allSavedSets.find(
      (s) => s.name.toLowerCase() === nameInput.toLowerCase()
    );
    const variables = {
      name: escape(nameInput),
      view,
      filters: currentFiltersUrl(),
    };
    if (existing) {
      setItemToOverwrite(existing);
      setIsLoading(false);
      return;
    }
    createSavedSet(variables)
      .then((res) => {
        if (res.error) {
          showToast('Error creating filter set', 'error');
        } else {
          showToast('Filter set created', 'success');
        }
      })
      .finally(() => {
        delay(() => {
          setIsLoading(false);
          onClose();
          onCreated?.();
        }, 100);
      });
  };

  return itemToOverwrite ? (
    <>
      {overwriteSlot ? (
        overwriteSlot(itemToOverwrite)
      ) : (
        <Text fontSize="sm">
          Are you sure you want to overwrite this saved set?
        </Text>
      )}
      <ButtonGroup size="sm" className={styles.overwriteActions}>
        <Button
          variant="ghost"
          color="secondary"
          onClick={() => setItemToOverwrite(undefined)}
        >
          Cancel
        </Button>
        <Button
          color="destructive"
          loading={isLoading}
          onClick={handleUpdateFilterSet}
        >
          Overwrite
        </Button>
      </ButtonGroup>
    </>
  ) : (
    <>
      <Autocomplete
        dataTestId="filter-set-name-input"
        inputId="filter-set-name"
        placeholder="Enter a set name"
        inputValue={nameInput}
        setInputValue={setNameInput}
        suggestions={suggestions}
        dropdownHeader="Matching Sets"
        initialFocusRef={initialFocusRef}
        comboBoxProps={{
          items: suggestions,
          inputValue: nameInput,
          onInputValueChange: ({ inputValue }) => {
            const value = inputValue ?? '';
            const filtered = allSavedSets.filter((set) => {
              if (isEmpty(value)) return false;
              return set.name
                .toLowerCase()
                .includes(value.toLowerCase().trim());
            });
            setSuggestions(filtered);
            setNameInput(value);
          },
          itemToString,
        }}
      />
      <ButtonGroup size="sm" className={styles.saveActions}>
        <Button size="sm" variant="ghost" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={nameInput.length === 0 || isLoading || isDisabled}
          onClick={handleCreate}
          loading={isLoading}
          data-testid="filter-set-save-button"
        >
          Save
        </Button>
      </ButtonGroup>
    </>
  );
};
